import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Card, CardContent, CardKicker } from './Card';

export interface KpiDelta {
  /** Variação numérica (positiva ou negativa). Sinal indica direção. */
  value: number;
  /** Label do período de comparação (ex.: "vs. mês anterior"). */
  label: string;
}

/**
 * `title`/`value` são do KPI; o resto das props vai para a moldura — o primeiro
 * consumidor real (E54.F4) identifica cada card por `data-testid`, e sem o
 * repasse ele teria de embrulhar o primitivo num `div` só para carimbar o
 * atributo.
 */
export interface KpiCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string;
  value: ReactNode;
  /** Delta vs. período anterior. Omitir quando backend não entregar — NÃO inventar. */
  delta?: KpiDelta | null;
  icon?: LucideIcon;
}

function DeltaRow({ delta }: { delta: KpiDelta }) {
  const direction: 'up' | 'down' | 'flat' =
    delta.value > 0 ? 'up' : delta.value < 0 ? 'down' : 'flat';
  const colorClass =
    direction === 'up'
      ? 'text-success-700'
      : direction === 'down'
        ? 'text-danger-700'
        : 'text-fg-muted';
  const sign = delta.value > 0 ? '+' : '';
  return (
    <div className={cn('flex items-center gap-1 text-caption', colorClass)} data-testid="kpi-delta">
      {direction === 'up' ? <ArrowUpRight className="h-3 w-3" aria-hidden /> : null}
      {direction === 'down' ? <ArrowDownRight className="h-3 w-3" aria-hidden /> : null}
      <span>
        {sign}
        {delta.value} {delta.label}
      </span>
    </div>
  );
}

export function KpiCard({ title, value, delta, icon: Icon, className, ...props }: KpiCardProps) {
  return (
    <Card className={cn('flex h-full flex-col', className)} {...props}>
      {/* `pt-sc-6` explícito, não `p-sc-6`: o `CardContent` nasce `p-sc-6 pt-0`
          porque o respiro de cima costuma ser do `CardHeader` — e aqui não há
          header. `p-*` e `pt-*` são grupos distintos para o twMerge, então um
          `p-sc-6` passado por fora **não** apaga o `pt-0` e o texto encosta na
          borda de cima do cartão. */}
      <CardContent className="flex flex-1 flex-col gap-sc-3 p-sc-6 pt-sc-6">
        <div className="flex items-start justify-between gap-2">
          <CardKicker>{title}</CardKicker>
          {Icon ? <Icon className="h-4 w-4 text-fg-faint" aria-hidden /> : null}
        </div>
        {/* Número do KPI é dado pericial: mono, no degrau de 34px da prancha 1B. */}
        <div className="font-mono text-display-md text-fg">{value}</div>
        {delta != null ? <DeltaRow delta={delta} /> : null}
      </CardContent>
    </Card>
  );
}
